/**
 * Доступ к камере: единственное место, где приложение спрашивает разрешение и
 * понимает, ПОЧЕМУ камера может быть недоступна. Нужно, чтобы монтажник не
 * гадал — экран всегда показывает ему одну понятную кнопку.
 *
 * Живая камера через getUserMedia работает только в защищённом контексте
 * (HTTPS или localhost). На планшете/телефоне, открытом по http:// в локальной
 * сети, браузер вообще не отдаёт mediaDevices — тогда единственный рабочий путь
 * это `<input type="file" capture>`, который открывает камеру устройства
 * напрямую. Это состояние мы распознаём отдельно и не пугаем «камера сломалась».
 */

export type CameraState =
  | 'granted' // доступ есть — можно включать поток
  | 'denied' // пользователь запретил — включить снова только в настройках браузера
  | 'prompt' // ещё не спрашивали — покажется системный запрос
  | 'insecure' // http:// без localhost: живой камеры не будет, только съёмка файлом
  | 'unsupported' // браузер без getUserMedia

export interface CameraSupport {
  secure: boolean
  hasGetUserMedia: boolean
}

export function cameraSupport(): CameraSupport {
  return {
    secure: window.isSecureContext,
    hasGetUserMedia: Boolean(navigator.mediaDevices?.getUserMedia),
  }
}

/**
 * Состояние доступа без обращения к камере — по контексту и Permissions API.
 * Не показывает системный запрос; для этого есть requestCamera/preflight.
 */
export async function queryCameraState(): Promise<CameraState> {
  const { secure, hasGetUserMedia } = cameraSupport()
  if (!hasGetUserMedia) return secure ? 'unsupported' : 'insecure'
  // Permissions API поддержан не везде (в части версий Safari — частично).
  // Тогда считаем, что нужно спросить: системный запрос сам всё покажет.
  const permissions = navigator.permissions
  if (!permissions?.query) return 'prompt'
  try {
    const status = await permissions.query({ name: 'camera' as PermissionName })
    return status.state as CameraState
  } catch {
    return 'prompt'
  }
}

export interface CameraFailure {
  state: CameraState
  message: string
}

/** Разбирает ошибку getUserMedia в понятное монтажнику состояние и текст. */
export function describeCameraError(err: unknown): CameraFailure {
  const name = err instanceof DOMException ? err.name : ''
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return {
      state: 'denied',
      message:
        'Доступ к камере запрещён. Нажмите «Разрешить камеру», а если запрос не ' +
        'появился — включите камеру для этого сайта в настройках браузера.',
    }
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return {
      state: 'unsupported',
      message: 'Камера на устройстве не найдена. Снимите паспорт через камеру устройства.',
    }
  }
  return {
    state: cameraSupport().secure ? 'unsupported' : 'insecure',
    message:
      'Камеру не удалось включить в браузере. Снимите паспорт через камеру устройства — ' +
      'кнопка ниже.',
  }
}

/**
 * Запрашивает доступ и возвращает поток. Бросает при отказе/недоступности.
 * Остановить дорожки (getTracks().stop()) обязан вызывающий.
 */
export async function requestCameraStream(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new DOMException('getUserMedia unavailable', 'NotSupportedError')
  }
  return navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1920 },
      height: { ideal: 1440 },
    },
    audio: false,
  })
}

/**
 * Проактивный запрос разрешения: включает камеру и тут же гасит поток. Нужен,
 * чтобы системный запрос всплыл ЗАРАНЕЕ (сразу после входа), а не посреди
 * оформления заявки. В незащищённом контексте молча ничего не делает — просить
 * там нечего, сработает только съёмка через файл.
 */
export async function preflightCameraPermission(): Promise<CameraState> {
  const state = await queryCameraState()
  if (state !== 'prompt') return state
  try {
    const stream = await requestCameraStream()
    stream.getTracks().forEach((track) => track.stop())
    return 'granted'
  } catch (err) {
    return describeCameraError(err).state
  }
}

/**
 * Подписка на смену разрешения (Permissions API). Возвращает функцию отписки.
 * Даёт авто-восстановление: монтажник включил камеру в настройках браузера —
 * экран сам это заметит, без перезагрузки страницы.
 */
export function onCameraPermissionChange(cb: (state: CameraState) => void): () => void {
  const permissions = navigator.permissions
  if (!permissions?.query) return () => undefined
  let status: PermissionStatus | null = null
  let disposed = false
  const handler = () => {
    if (status) cb(status.state as CameraState)
  }
  permissions
    .query({ name: 'camera' as PermissionName })
    .then((s) => {
      if (disposed) return
      status = s
      s.addEventListener('change', handler)
    })
    .catch(() => undefined)
  return () => {
    disposed = true
    status?.removeEventListener('change', handler)
  }
}
